import { useEffect, useState } from 'react';
import Modal from '../../components/common/Modal';
import { createUser, updateUser } from '../../services/userService';
import { CLASS_LEVELS, CLASS_NAMES, USER_ROLES } from '../../utils/constants';

const UserFormModal = ({ user, onClose }) => {
  const isEditMode = !!user;

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    role: USER_ROLES.STUDENT,
    classLevel: CLASS_LEVELS.SS1,
    assignedSubjects: [],
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.fullName || '',
        email: user.email || '',
        password: '',
        role: user.role || USER_ROLES.STUDENT,
        classLevel: user.classLevel || CLASS_LEVELS.SS1,
        assignedSubjects: user.assignedSubjects || [],
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.fullName || !formData.email) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    if (!isEditMode && !formData.password) {
      setError('Password is required for new users');
      setLoading(false);
      return;
    }

    try {
      if (isEditMode) {
        await updateUser(user.id, {
          fullName: formData.fullName,
          role: formData.role,
          classLevel: formData.classLevel,
          assignedSubjects: formData.assignedSubjects,
          isActive: formData.isActive,
        });
      } else {
        await createUser(formData);
      }

      onClose();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEditMode ? 'Edit User' : 'Add New User'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Full Name */}
        <div>
          <label htmlFor="fullName" className="block text-sm font-semibold text-gray-900 mb-2">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            placeholder="Enter full name"
            required
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Enter email address"
            required
            disabled={loading || isEditMode}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          {isEditMode && (
            <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
          )}
        </div>

        {/* Password (only for new users) */}
        {!isEditMode && (
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-2">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter password"
              required
              disabled={loading}
              minLength={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
          </div>
        )}

        {/* Role */}
        <div>
          <label htmlFor="role" className="block text-sm font-semibold text-gray-900 mb-2">
            Role <span className="text-red-500">*</span>
          </label>
          <select
            id="role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            required
            disabled={loading}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            <option value={USER_ROLES.STUDENT}>Student</option>
            <option value={USER_ROLES.TEACHER}>Teacher</option>
            <option value={USER_ROLES.EDITOR}>Editor</option>
            <option value={USER_ROLES.ADMIN}>Admin</option>
          </select>
        </div>

        {/* Class Level (for students) */}
        {formData.role === USER_ROLES.STUDENT && (
          <div>
            <label htmlFor="classLevel" className="block text-sm font-semibold text-gray-900 mb-2">
              Class Level <span className="text-red-500">*</span>
            </label>
            <select
              id="classLevel"
              name="classLevel"
              value={formData.classLevel}
              onChange={handleChange}
              required
              disabled={loading}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value={CLASS_LEVELS.JSS1}>{CLASS_NAMES.jss1}</option>
              <option value={CLASS_LEVELS.JSS2}>{CLASS_NAMES.jss2}</option>
              <option value={CLASS_LEVELS.JSS3}>{CLASS_NAMES.jss3}</option>
              <option value={CLASS_LEVELS.SS1}>{CLASS_NAMES.ss1}</option>
              <option value={CLASS_LEVELS.SS2}>{CLASS_NAMES.ss2}</option>
              <option value={CLASS_LEVELS.SS3}>{CLASS_NAMES.ss3}</option>
              <option value={CLASS_LEVELS.UTME}>{CLASS_NAMES.utme}</option>
            </select>
          </div>
        )}

        {/* Active Status */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isActive"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            disabled={loading}
            className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary/20 cursor-pointer"
          />
          <label htmlFor="isActive" className="text-sm font-medium text-gray-900 cursor-pointer">
            Active User
          </label>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button 
            type="submit" 
            disabled={loading}
            className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UserFormModal;
